/**
 * Latency maths (W1-14): output-latency detection from the AudioContext (feature-detected: Chrome 102+ and
 * Firefox 70+ expose `outputLatency`, Safari does not), the one-time latency hint threshold (D§5.2) and the
 * tap-along calibration statistics (A§8: "tap along to 16 clicks; median offset -> calibration ms"): tap matching
 * with an asymmetric window and phase resolution from the circular mean of the taps, median of the errors.
 */

/** Hint threshold (D§5.2): offer calibration when outputLatency + baseLatency exceeds this, or is unknown. */
export const LATENCY_HINT_THRESHOLD_MS = 50;

/** Interval between calibration clicks (100 BPM). */
export const CALIBRATION_INTERVAL_SEC = 0.6;

/** Count-in clicks played before taps are counted. */
export const CALIBRATION_COUNT_IN = 4;

/**
 * Accepted tap errors (tap minus heard click), as fractions of the interval: from 25 % early to 75 % late. The
 * residual latency that calibration measures is almost always positive and can be large (Bluetooth headphones add
 * 150-300 ms and Safari reports no outputLatency, so none of it is compensated), while people tapping along lead a
 * click by a few tens of ms. A symmetric window would fold residuals above half the interval into negative values.
 * With a 600 ms interval: -150 ms to +450 ms.
 */
export const CALIBRATION_EARLIEST_FRACTION = 0.25;
export const CALIBRATION_LATEST_FRACTION = 0.75;

/** A tap further than this fraction of the interval from the session's typical error is a stray tap. */
export const CALIBRATION_MAX_DEVIATION_FRACTION = 1 / 3;

/**
 * Minimum phase consistency (mean resultant length of the taps' phases on the click grid, 1 = identical errors,
 * about 1/sqrt(n) for random taps) for a usable session: 0.5 corresponds to a tap scatter of about 110 ms at 100 BPM.
 */
export const CALIBRATION_MIN_CONSISTENCY = 0.5;

/** What a context exposes about its latency (fields absent when the browser does not report them). */
export interface LatencyReport {
  /** `AudioContext.outputLatency` in seconds, or null. */
  readonly outputSec: number | null;
  /** `AudioContext.baseLatency` in seconds, or null. */
  readonly baseSec: number | null;
}

function finiteNonNegative(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;
}

/** Reads `outputLatency` / `baseLatency` without assuming either exists. */
export function readLatency(ctx: object | null): LatencyReport {
  if (ctx === null) return { outputSec: null, baseSec: null };
  const c = ctx as { readonly outputLatency?: unknown; readonly baseLatency?: unknown };
  return { outputSec: finiteNonNegative(c.outputLatency), baseSec: finiteNonNegative(c.baseLatency) };
}

/** Detected device latency in ms (outputLatency + baseLatency); null when the browser exposes no outputLatency. */
export function detectedLatencyMsOf(report: LatencyReport): number | null {
  if (report.outputSec === null) return null;
  return (report.outputSec + (report.baseSec ?? 0)) * 1000;
}

/** Readings the held estimate is the median of. */
export const LATENCY_ESTIMATE_WINDOW = 9;
/** The held estimate moves only when the median moves by more than this (ms). */
export const LATENCY_ESTIMATE_HYSTERESIS_MS = 10;
/** How often the engine takes a reading into the held estimate while a context exists (UX-073). */
export const LATENCY_POLL_MS = 250;

/**
 * A steady device-latency estimate (UX-073): browsers report `outputLatency` with jitter (51, 59, 43, 35 ms in one
 * session), so the number shown is the median of the last readings, rounded to whole ms, and it only changes when
 * that median moves by more than the hysteresis. A device change (null, then a new value) starts over.
 */
export class StableLatency {
  private readonly samples: number[] = [];
  private held: number | null = null;
  private readonly window: number;
  private readonly hysteresisMs: number;

  constructor(window: number = LATENCY_ESTIMATE_WINDOW, hysteresisMs: number = LATENCY_ESTIMATE_HYSTERESIS_MS) {
    this.window = window;
    this.hysteresisMs = hysteresisMs;
  }

  /** Adds a reading (ms, or null when the browser reports none) and returns the held estimate. */
  read(ms: number | null): number | null {
    if (ms === null || !Number.isFinite(ms)) {
      this.samples.length = 0;
      this.held = null;
      return null;
    }
    this.samples.push(ms);
    if (this.samples.length > this.window) this.samples.shift();
    const m = Math.round(median(this.samples));
    if (this.held === null || Math.abs(m - this.held) > this.hysteresisMs) this.held = m;
    return this.held;
  }
}

/**
 * Seconds to subtract from `currentTime` to get the audible context time when no output timestamp is available:
 * outputLatency when exposed, otherwise baseLatency, otherwise 0.
 */
export function fallbackLatencySec(report: LatencyReport): number {
  return report.outputSec ?? report.baseSec ?? 0;
}

/** Whether the one-time "your audio device delays sound" hint should be offered (D§5.2). */
export function needsLatencyHint(detectedMs: number | null): boolean {
  return detectedMs === null || detectedMs > LATENCY_HINT_THRESHOLD_MS;
}

/** Median of a non-empty list (mean of the two middle values for even lengths); NaN for an empty list. */
export function median(values: readonly number[]): number {
  if (values.length === 0) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  const hi = sorted[mid] ?? NaN;
  return sorted.length % 2 === 1 ? hi : ((sorted[mid - 1] ?? NaN) + hi) / 2;
}

/** Centre of the default matching window (ms): taps from 25 % early to 75 % late belong to a click. */
export function defaultCenterMs(intervalMs: number): number {
  return ((CALIBRATION_LATEST_FRACTION - CALIBRATION_EARLIEST_FRACTION) / 2) * intervalMs;
}

/**
 * Matches a tap (perf ms) to a click (click i heard at `firstHeardMs + i * intervalMs`, `clickCount` clicks): the
 * click whose error `tap - click` lies in [centerMs - interval / 2, centerMs + interval / 2). The default centre
 * gives the asymmetric window of CALIBRATION_EARLIEST_FRACTION / CALIBRATION_LATEST_FRACTION. Returns null before
 * the first or after the last click.
 */
export function matchTap(
  tapMs: number,
  firstHeardMs: number,
  intervalMs: number,
  clickCount: number,
  centerMs: number = defaultCenterMs(intervalMs),
): { readonly index: number; readonly errorMs: number } | null {
  if (!(intervalMs > 0) || clickCount <= 0 || !Number.isFinite(tapMs)) return null;
  const index = Math.floor((tapMs - firstHeardMs - centerMs) / intervalMs + 0.5);
  if (index < 0 || index >= clickCount) return null;
  return { index, errorMs: tapMs - (firstHeardMs + index * intervalMs) };
}

export interface TapResolution {
  /** One error (ms) per matched click, in click order. */
  readonly errorsMs: readonly number[];
  /** Typical error (ms) the taps were matched around: the circular mean of their phases, unwrapped. */
  readonly centerMs: number;
  /** Mean resultant length of the tap phases (0..1). */
  readonly consistency: number;
  /** Taps after the count-in (matched or not). */
  readonly tapCount: number;
}

/**
 * Resolves a calibration session's taps. The phase of the click grid is ambiguous by whole intervals, so the
 * session's typical error is taken from the circular mean of all tap phases and unwrapped into the accepted range
 * (25 % early .. 75 % late); every tap is then matched to the click it is closest to around that error, which keeps
 * a session consistent even when its residual is near the edge of the range (taps scattering across it would
 * otherwise split between two clicks). Taps during the count-in, stray taps (further than a third of the interval
 * from the typical error) and second taps on one click (the one further from the typical error) are dropped.
 */
export function resolveTaps(
  tapsMs: readonly number[],
  firstHeardMs: number,
  intervalMs: number,
  clickCount: number,
  countIn: number,
): TapResolution {
  const empty: TapResolution = { errorsMs: [], centerMs: NaN, consistency: 0, tapCount: 0 };
  if (!(intervalMs > 0) || clickCount <= countIn) return empty;
  const lo = firstHeardMs + (countIn - CALIBRATION_EARLIEST_FRACTION) * intervalMs;
  const hi = firstHeardMs + (clickCount - 1 + CALIBRATION_LATEST_FRACTION) * intervalMs;
  const taps = tapsMs.filter((t) => Number.isFinite(t) && t >= lo && t < hi);
  if (taps.length === 0) return empty;

  let sx = 0;
  let sy = 0;
  for (const t of taps) {
    const a = (2 * Math.PI * (t - firstHeardMs)) / intervalMs;
    sx += Math.cos(a);
    sy += Math.sin(a);
  }
  const consistency = Math.hypot(sx, sy) / taps.length;
  let phase = ((Math.atan2(sy, sx) / (2 * Math.PI)) * intervalMs + intervalMs) % intervalMs;
  if (phase >= CALIBRATION_LATEST_FRACTION * intervalMs) phase -= intervalMs;
  const centerMs = phase;

  const maxDev = CALIBRATION_MAX_DEVIATION_FRACTION * intervalMs;
  const best = new Map<number, number>();
  for (const t of taps) {
    const m = matchTap(t, firstHeardMs, intervalMs, clickCount, centerMs);
    if (m === null || m.index < countIn) continue;
    const dev = Math.abs(m.errorMs - centerMs);
    if (dev > maxDev) continue;
    const prev = best.get(m.index);
    if (prev === undefined || dev < Math.abs(prev - centerMs)) best.set(m.index, m.errorMs);
  }
  const errorsMs = [...best.entries()].sort((a, b) => a[0] - b[0]).map((e) => e[1]);
  return { errorsMs, centerMs, consistency, tapCount: taps.length };
}

/**
 * Calibration result from per-click tap errors (ms): the median, rounded to whole ms. Positive = the sound is
 * heard later than the detected latency predicts (visuals must be delayed further).
 */
export function calibrationFromErrors(errorsMs: readonly number[]): number {
  const m = median(errorsMs);
  if (Number.isNaN(m)) return 0;
  const r = Math.round(m);
  return r === 0 ? 0 : r; // never -0
}

/** Minimum number of matched taps for a usable result when the click train ran out before `taps` were collected. */
export function minUsableTaps(taps: number): number {
  return Math.max(4, Math.ceil(taps / 2));
}
